import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import generateID from "utils/generateID";

import { Box, IconButton } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

import { Task } from "api/types/users";
import { UserTaskType } from "../Interfaces";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import useMessageListener from "../hooks/useMessageListener";
import { getTaskDisplayName } from "../utils/getTaskDisplayName";

const EMBED_ID = generateID("fillout-");

interface FilloutProps {
  data: Task;
  setCurrentTask: (task: Task | null) => void;
  onSubmit: (value?: any) => void;
}

/**
 *
 * @description The `FilloutTask` component is a modal that displays a Fillout iframe.
 * It listens for the `form_submit` event and then calls the `onSubmit` function to mark the task as completed.
 *
 * @param {FilloutProps} props - The props for the FilloutTask component
 * @param {Task} props.data - The task data for the FilloutTask component
 * @param {Function} props.setCurrentTask - The function to set the current task
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * This function will be called when the Fillout form is submitted.
 * @returns {JSX.Element} - The FilloutTask component
 */
const FilloutTask = ({ data, setCurrentTask, onSubmit }: FilloutProps) => {
  const { t } = useTranslation(["dashboard"]);
  const { type: taskType, id: taskId } = data;
  const isCaregiverSurvey = data?.metadata?.formType === "CAREGIVER_SATISFACTION_SURVEY";
  const embedId = `${EMBED_ID}-${taskId}`;

  // All hooks must be called unconditionally before any early returns.
  const [open, setOpen] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // We fullscreen the wrapper (not the iframe itself) so the exit toggle stays
  // visible on top of the form while in fullscreen.
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // iPhone Safari implements no element-level Fullscreen API — only
  // HTMLVideoElement.webkitEnterFullscreen — so requestFullscreen is undefined
  // there and tapping the control would throw instead of expanding the form.
  // That is exactly the small-screen case this control exists for, so detect
  // support and hide the button rather than offering one that errors.
  const fullscreenSupported =
    typeof document !== "undefined" &&
    document.fullscreenEnabled === true &&
    typeof document.documentElement.requestFullscreen === "function";

  // Keep the toggle icon in sync with the actual fullscreen state — the user
  // can also leave fullscreen via Esc / the browser chrome, which doesn't go
  // through our button.
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenContainerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    // Both calls return promises that reject on their own (e.g. the request
    // wasn't treated as a user gesture). Swallow that: failing to resize a form
    // shouldn't surface as an unhandled rejection, and the fullscreenchange
    // listener above keeps the icon truthful either way.
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
    } else {
      void fullscreenContainerRef.current?.requestFullscreen()?.catch(() => {});
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTransitionExited = () => {
    setCurrentTask(null);
  };

  useMessageListener({
    embedId,
    eventName: "form_submit",
    fn: async () => {
      await onSubmit(taskId);
      handleClose();
    },
  });

  // Early returns after all hooks.
  if (taskType !== UserTaskType.FILLOUT_IFRAME) {
    console.warn(`FilloutTask: Task type ${taskType} is not supported`);
    return null;
  }

  // Guard and parse the URL — malformed/relative values would throw inside
  // new URL(), so we wrap and bail cleanly rather than crashing render.
  let iframeURL: URL | null = null;
  try {
    if (data?.metadata?.url) {
      iframeURL = new URL(data.metadata.url);
    }
  } catch {
    console.error(`FilloutTask: malformed URL for task ${taskId}:`, data?.metadata?.url);
  }

  if (!iframeURL) {
    console.error(`FilloutTask: no valid URL for task ${taskId}`);
    return null;
  }

  iframeURL.searchParams.set("fillout-embed-id", embedId);
  iframeURL.searchParams.set("task-id", `${taskId}`);

  if (!isCaregiverSurvey) {
    if (!data?.metadata?.guid) {
      console.error(`FilloutTask: guid missing for task ${taskId} (formType: ${data?.metadata?.formType ?? "unknown"})`);
      return null;
    }
    iframeURL.searchParams.set("guid", data.metadata.guid);
  }

  return (
    <ResponsiveModal
      open={open}
      title={getTaskDisplayName(data, t)}
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      maxWidth="lg"
      onTransitionExited={handleTransitionExited}
      dialogSx={{
        height: "80vh",
        padding: 0,
        "& > .MuiBox-root > .MuiBox-root:first-of-type": {
          padding: "16px 24px",
          margin: 0
        },
      }}
      drawerSx={{
        height: "80vh",
      }}
      drawerContentSx={{
        height: "calc(100% - 54px)",
        overflowY: "hidden",
        padding: 0,
      }}
      dialogContentSx={{
        height: "80vh",
        overflowY: "hidden",
      }}
    >
      <Box
        ref={fullscreenContainerRef}
        sx={{ position: "relative", width: "100%", height: "100%", backgroundColor: "#fff" }}
      >
        {fullscreenSupported && (
          <IconButton
            onClick={toggleFullscreen}
            size="small"
            aria-label={t(
              isFullscreen
                ? "parent.myTasks.common.exitFullscreen"
                : "parent.myTasks.common.enterFullscreen",
            )}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: "rgba(255,255,255,0.85)",
              "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
            }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        )}
        <iframe
          id="fillout-iframe"
          ref={iframeRef}
          src={iframeURL.toString()}
          width="100%"
          height="100%"
          allow="fullscreen"
          allowFullScreen
          style={{ border: "none" }}
        />
      </Box>
    </ResponsiveModal>
  );
};

export default FilloutTask;
